import { createTheme, ThemeProvider } from "@mui/material";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import App from "./App";
import "./index.css";
import reportWebVitals from "./reportWebVitals";

const theme = createTheme({
  spacing: 8,

  palette: {
    primary: {
      main: "#1A2F6E",
      light: "#4F6EF7",
      dark: "#0D1B4B",
    },
    secondary: {
      main: "#4F6EF7",
    },
    background: {
      default: "#F8FAFF",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#0D1B4B",
      secondary: "#64748B",
    },
  },

  typography: {
    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
    h1: { fontWeight: 800, letterSpacing: "-1.5px", lineHeight: 1.1 },
    h2: { fontWeight: 800, letterSpacing: "-1px", lineHeight: 1.15 },
    h3: { fontWeight: 700, letterSpacing: "-0.5px", lineHeight: 1.25 },
    h4: { fontWeight: 700, letterSpacing: "-0.3px", lineHeight: 1.3 },
    h5: { fontWeight: 600, lineHeight: 1.4 },
    h6: { fontWeight: 600, lineHeight: 1.5 },
    body1: { lineHeight: 1.75, fontSize: "1rem" },
    body2: { lineHeight: 1.7, fontSize: "0.9rem" },
    button: { fontWeight: 600, textTransform: "none", letterSpacing: "0.01em" },
  },

  shape: {
    borderRadius: 14,
  },

  shadows: [
    "none",
    "0 1px 3px rgba(13,27,75,0.06)",
    "0 2px 8px rgba(13,27,75,0.08)",
    "0 4px 16px rgba(13,27,75,0.1)",
    "0 8px 24px rgba(13,27,75,0.1)",
    "0 12px 32px rgba(13,27,75,0.12)",
    "0 16px 48px rgba(13,27,75,0.12)",
    "0 20px 60px rgba(13,27,75,0.14)",
    "0 24px 72px rgba(13,27,75,0.16)",
    "0 28px 80px rgba(13,27,75,0.16)",
    "0 32px 88px rgba(13,27,75,0.18)",
    "0 36px 96px rgba(13,27,75,0.18)",
    "0 40px 104px rgba(13,27,75,0.20)",
    "0 44px 112px rgba(13,27,75,0.20)",
    "0 48px 120px rgba(13,27,75,0.22)",
    "0 52px 128px rgba(13,27,75,0.22)",
    "0 56px 136px rgba(13,27,75,0.24)",
    "0 60px 144px rgba(13,27,75,0.24)",
    "0 64px 152px rgba(13,27,75,0.26)",
    "0 68px 160px rgba(13,27,75,0.26)",
    "0 72px 168px rgba(13,27,75,0.28)",
    "0 76px 176px rgba(13,27,75,0.28)",
    "0 80px 184px rgba(13,27,75,0.30)",
    "0 84px 192px rgba(13,27,75,0.30)",
    "0 90px 200px rgba(13,27,75,0.32)",
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#F8FAFF",
          overflowX: "hidden",
        },
      },
    },

    MuiContainer: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "50px",
          fontWeight: 600,
          padding: "10px 28px",
          fontSize: "0.95rem",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(79,110,247,0.35)",
            transform: "translateY(-2px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        contained: {
          background: "linear-gradient(135deg, #1A2F6E 0%, #4F6EF7 100%)",
          color: "#fff",
          "&:hover": {
            background: "linear-gradient(135deg, #0D1B4B 0%, #3A5AE8 100%)",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #1A2F6E 0%, #4F6EF7 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #0D1B4B 0%, #3A5AE8 100%)",
          },
        },
        outlined: {
          borderWidth: "2px",
          "&:hover": {
            borderWidth: "2px",
            backgroundColor: "rgba(79,110,247,0.06)",
          },
        },
        sizeLarge: {
          padding: "13px 36px",
          fontSize: "1rem",
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "20px",
          border: "1px solid rgba(13,27,75,0.07)",
          boxShadow: "0 4px 24px rgba(13,27,75,0.07)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: "0 20px 60px rgba(13,27,75,0.13)",
            borderColor: "rgba(79,110,247,0.2)",
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "20px",
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            backgroundColor: "#fff",
            transition: "all 0.2s ease",
            "& fieldset": {
              borderColor: "rgba(13,27,75,0.12)",
              borderWidth: "1.5px",
            },
            "&:hover fieldset": {
              borderColor: "rgba(79,110,247,0.5)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#4F6EF7",
              borderWidth: "2px",
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "0.8rem",
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(13,27,75,0.08)",
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "16px",
          boxShadow: "0 16px 48px rgba(13,27,75,0.15)",
          border: "1px solid rgba(13,27,75,0.07)",
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: "16px !important",
          "&:before": { display: "none" },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <Router>
        <App />
      </Router>
    </ThemeProvider>
  </React.StrictMode>
);

reportWebVitals();
